/**
 * Permission constants matching the API's RequiresProjectPermission attributes
 *
 * These constants must stay in sync with the API permission strings.
 * Only permissions that have corresponding API endpoints are defined here.
 */

// Role presets for projects
export const PROJECT_ROLES = {
  OWNER: 'OWNER',    // Full access including access management and deletion
  EDITOR: 'EDITOR',  // Content and product editing
  VIEWER: 'VIEWER',  // Read-only access
} as const;

// Permission constants grouped by module
export const PERMISSIONS = {
  // Project permissions (global + project-scoped)
  PROJECT: {
    LIST: 'project.list',
    VIEW: 'project.view',
    CREATE: 'project.create',
    EDIT: 'project.edit',
    DELETE: 'project.delete',
    MANAGE: 'project.manage',
    SETTINGS: 'project.settings',
  },

  // User permissions (global)
  USER: {
    LIST: 'user.list',
    VIEW: 'user.view',
    CREATE: 'user.create',
    EDIT: 'user.edit',
    DELETE: 'user.delete',
  },

  // Content permissions (project-scoped) — API uses view/manage
  CONTENT: {
    VIEW: 'content.view',
    MANAGE: 'content.manage',
  },

  // E-shop permissions (project-scoped)
  ESHOP: {
    VIEW: 'eshop.view',
    MANAGE: 'eshop.manage',
    EDIT: 'eshop.edit',
    CUSTOMERS: 'eshop.customers',
    ORDERS: 'eshop.orders',
  },

  // Aurum permissions (project-scoped)
  AURUM: {
    CHECKIN: 'aurum.checkin',
  },

  // Subscription permissions (project-scoped)
  SUBSCRIPTION: {
    VIEW: 'subscription.view',
    MANAGE: 'subscription.manage',
  },

  // Invoicing permissions (project-scoped)
  INVOICING: {
    VIEW: 'invoicing.view',
    CREATE: 'invoicing.create',
    EDIT: 'invoicing.edit',
    DELETE: 'invoicing.delete',
    EXPORT: 'invoicing.export',
    MANAGE: 'invoicing.manage',
    CLIENTS: 'invoicing.clients',
  },

  // Media permissions (project-scoped) — API uses view/manage
  MEDIA: {
    VIEW: 'media.view',
    MANAGE: 'media.manage',
  },

  // Settings permissions (project-scoped) — email templates, forms
  SETTINGS: {
    VIEW: 'settings.view',
    MANAGE: 'settings.manage',
  },

  // Orchestration permissions (project-scoped)
  ORCHESTRATION: {
    VIEW: 'orchestration.view',
    MANAGE: 'orchestration.manage',
  },

  // Ticket permissions (project-scoped)
  TICKETS: {
    LIST: 'tickets.list',
    VIEW: 'tickets.view',
    CREATE: 'tickets.create',
    EDIT: 'tickets.edit',
    DELETE: 'tickets.delete',
    ASSIGN: 'tickets.assign',
  },

  // Audit Log permissions (global)
  AUDIT: {
    VIEW: 'audit.view',
  },
} as const;

// Wildcard for all permissions
export const ALL_PERMISSIONS = '*';

// Role-to-permissions mapping
export const ROLE_PERMISSIONS_MAP = {
  [PROJECT_ROLES.OWNER]: [
    PERMISSIONS.PROJECT.VIEW,
    PERMISSIONS.PROJECT.EDIT,
    PERMISSIONS.PROJECT.DELETE,
    PERMISSIONS.PROJECT.MANAGE,
    PERMISSIONS.PROJECT.SETTINGS,
    PERMISSIONS.CONTENT.VIEW,
    PERMISSIONS.CONTENT.MANAGE,
    PERMISSIONS.MEDIA.VIEW,
    PERMISSIONS.MEDIA.MANAGE,
    PERMISSIONS.ESHOP.VIEW,
    PERMISSIONS.ESHOP.MANAGE,
    PERMISSIONS.ESHOP.EDIT,
    PERMISSIONS.ESHOP.CUSTOMERS,
    PERMISSIONS.ESHOP.ORDERS,
    PERMISSIONS.SUBSCRIPTION.VIEW,
    PERMISSIONS.SUBSCRIPTION.MANAGE,
    PERMISSIONS.INVOICING.VIEW,
    PERMISSIONS.INVOICING.CREATE,
    PERMISSIONS.INVOICING.EDIT,
    PERMISSIONS.INVOICING.DELETE,
    PERMISSIONS.INVOICING.EXPORT,
    PERMISSIONS.INVOICING.MANAGE,
    PERMISSIONS.INVOICING.CLIENTS,
    PERMISSIONS.SETTINGS.VIEW,
    PERMISSIONS.SETTINGS.MANAGE,
    PERMISSIONS.ORCHESTRATION.VIEW,
    PERMISSIONS.ORCHESTRATION.MANAGE,
    PERMISSIONS.TICKETS.LIST,
    PERMISSIONS.TICKETS.VIEW,
    PERMISSIONS.TICKETS.CREATE,
    PERMISSIONS.TICKETS.EDIT,
    PERMISSIONS.TICKETS.DELETE,
    PERMISSIONS.TICKETS.ASSIGN,
  ],
  [PROJECT_ROLES.EDITOR]: [
    PERMISSIONS.PROJECT.VIEW,
    PERMISSIONS.PROJECT.EDIT,
    PERMISSIONS.CONTENT.VIEW,
    PERMISSIONS.CONTENT.MANAGE,
    PERMISSIONS.MEDIA.VIEW,
    PERMISSIONS.MEDIA.MANAGE,
    PERMISSIONS.ESHOP.VIEW,
    PERMISSIONS.ESHOP.MANAGE,
    PERMISSIONS.ESHOP.CUSTOMERS,
    PERMISSIONS.ESHOP.ORDERS,
    PERMISSIONS.SUBSCRIPTION.VIEW,
    PERMISSIONS.SUBSCRIPTION.MANAGE,
    PERMISSIONS.SETTINGS.VIEW,
    PERMISSIONS.TICKETS.LIST,
    PERMISSIONS.TICKETS.VIEW,
    PERMISSIONS.TICKETS.CREATE,
    PERMISSIONS.TICKETS.EDIT,
    PERMISSIONS.TICKETS.ASSIGN,
  ],
  [PROJECT_ROLES.VIEWER]: [
    PERMISSIONS.PROJECT.VIEW,
    PERMISSIONS.CONTENT.VIEW,
    PERMISSIONS.MEDIA.VIEW,
    PERMISSIONS.ESHOP.VIEW,
    PERMISSIONS.SUBSCRIPTION.VIEW,
    PERMISSIONS.SETTINGS.VIEW,
    PERMISSIONS.TICKETS.LIST,
    PERMISSIONS.TICKETS.VIEW,
  ],
};
